import { captureException } from '@sentry/react';
import { RouterContext } from 'react-resource-router';
import { createResource } from 'react-resource-router/resources';

import { ResourceContext } from '@townsquare/router';
import { isGateEnabled } from '@townsquare/stat-sig/gate';

import { FIRST_PAGE_SIZE } from '../constants';
import { fetchPopularFeed } from '../hooks/fetch-popular-feed';
import { getDiscoverTab, getTimeframe, type PopularFeedResponse, type Timeframe } from '../types';

const getVariables = (
  routerContext: RouterContext,
  resourceContext: ResourceContext,
): { isEnabled: false } | { isEnabled: true; cloudId: string; timeframe: Timeframe } => {
  const { collabContext } = resourceContext;

  // If the gate is off, disable
  if (!isGateEnabled('townsquare_enable_popular_feed')) {
    return { isEnabled: false };
  }

  // If tabs are enabled and we're in a tab, disable. That's handled by label-feed-resource
  const discoverTab = getDiscoverTab(routerContext.query.tab);
  if (isGateEnabled('townsquare_enable_popular_tabs') && discoverTab !== 'popular') {
    return { isEnabled: false };
  }

  // If no cloudId (yet), disable
  if (!collabContext || !collabContext.rovoWorkspace) {
    return { isEnabled: false };
  }

  return {
    isEnabled: true,
    cloudId: collabContext.rovoWorkspace,
    timeframe: getTimeframe(routerContext.query.timeGranularity),
  };
};

export const popularFeedResource = createResource<PopularFeedResponse | undefined>({
  getData: async (routerContext, resourceContext) => {
    const variables = getVariables(routerContext, resourceContext as ResourceContext);

    if (!variables.isEnabled) {
      return undefined;
    }

    // Load longer first page of results
    return fetchPopularFeed(variables.cloudId, variables.timeframe, FIRST_PAGE_SIZE).catch(error => {
      captureException(error);

      return undefined;
    });
  },
  getKey: (routerContext, resourceContext) => {
    const variables = getVariables(routerContext, resourceContext as ResourceContext);

    if (!variables.isEnabled) {
      return `popular-feed-posts-disabled`;
    }

    return `popular-feed-posts-key-${variables.timeframe}-${variables.cloudId}`;
  },
  type: 'POPULAR_FEED_POSTS',
  maxAge: 60_000 * 30, // Cache for 30 minutes
});
